// ============================================================================
// Receipt price breakdown — turns an interest preview into a line-by-line
// explanation the counter staff can read out (or hand over) to the customer.
// ----------------------------------------------------------------------------
// Pure functions, no React: `buildReceiptBreakdown` shapes the model and
// `receiptBreakdownPrintHtml` renders it as a standalone printable page. Every
// number comes from the engine's own output — this module only NAMES things and
// never re-derives a figure, so an explanation can never drift from the amount
// that gets booked.
//
// Inputs come straight off /receipts/calculate-interest (or the local mirror of
// it): interest_receivable / insurance_receivable + the audit fields
// (reference_date, due_date, extra_days, full_months, months_charged, rules…).
// The allocation half is computed by the shared engine's allocatePayment, i.e.
// the same code createReceipt/updateReceipt allocate with.
// ============================================================================
import dayjs from "dayjs";
import { allocatePayment } from "../../utils/pawnInterestCalculator";

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const money = (v) => `₹${num(v).toLocaleString("en-IN")}`;

const day = (d) =>
  d && dayjs(d).isValid() ? dayjs(d).format("DD MMM YYYY") : "—";

// 2 → "2 months", 2.5 → "2½ months", 1 → "1 month".
const monthsLabel = (m) => {
  const n = num(m);
  const whole = Math.floor(n);
  const half = n - whole >= 0.5;
  const text = `${whole > 0 ? whole : ""}${half ? "½" : ""}` || "0";
  return `${text} ${n === 1 ? "month" : "months"}`;
};

const plural = (n, word) => `${n} ${word}${Math.abs(num(n)) === 1 ? "" : "s"}`;

// "2-day grace period" (hyphenated adjective form, not "2 days grace").
const graceLabel = (days) => `${num(days)}-day grace period`;

const pct = (v) => `${num(v)}%`;

// ── Which rule set the charge, in the customer's language ────────────────────
// One entry per branch the engine can take (see computeChargePeriod). `title` is
// the headline; `detail` explains WHY these months/days were charged, naming the
// dates and the store setting responsible. This is the part a customer argues
// about, so it always states the rule, the dates and the count.
const explainRule = (p) => {
  const graceDays = num(p.rules?.graceDays);
  const monthly = p.rules?.monthly !== false;
  const due = day(p.due_date);
  const ref = day(p.reference_date);
  const extra = num(p.extra_days);
  const months = monthsLabel(p.months_charged);
  const fullMonths = num(p.full_months);

  switch (p.calc_branch) {
    case "prepaid-first-month":
      return {
        title: "First month was already paid at pledge time",
        detail: `One month's interest and insurance were deducted when the loan was given, so nothing more is due until ${due}.`,
      };
    case "loan-2-day-grace":
      return {
        title: `Cleared within the ${graceLabel(graceDays || 2)}`,
        detail: `The loan is being closed within ${plural(
          graceDays || 2,
          "day",
        )} of the pledge date, so no interest is charged at all.`,
      };
    case "already-covered":
      return {
        title: "Interest is already paid for this date",
        detail: `A previous receipt paid the interest through ${ref}, which is on or after this receipt's date — so no new interest or insurance is charged. Only amounts left unpaid earlier remain.`,
      };
    case "exact-months":
      return {
        title: `Paid exactly on the due date`,
        detail: `From ${ref} to ${due} is ${monthsLabel(
          fullMonths,
        )} — the payment lands on the due date, so exactly ${months} are charged.`,
      };
    case "grace-2-days":
      return {
        title: `${plural(extra, "day")} late — inside the ${graceLabel(
          graceDays,
        )}`,
        detail: `The due date was ${due}. Payment is ${plural(
          extra,
          "day",
        )} after it, and this store allows a grace of ${plural(
          graceDays,
          "day",
        )} — those days are free. Only ${months} are charged.`,
      };
    case "half-month":
      return {
        title: `${plural(
          extra,
          "day",
        )} past the due date — charged as half a month`,
        detail: `The due date was ${due}. Being within 15 days of it, the store charges only half a month extra, so ${months} in total.`,
      };
    case "minimum-one-month":
      return {
        title: "Less than a month — one month minimum applies",
        detail: `Only ${plural(
          Math.max(0, dayjs(p.upto_date).diff(dayjs(p.reference_date), "day")),
          "day",
        )} have passed since ${ref}, but interest is charged month-wise with a minimum of one month.`,
      };
    case "full-extra-month":
      return monthly
        ? {
            title: `${plural(
              extra,
              "day",
            )} past the due date — one full extra month`,
            detail: `The due date was ${due}. Payment is ${plural(
              extra,
              "day",
            )} after it${
              graceDays > 0 ? `, beyond the ${graceLabel(graceDays)}` : ""
            }, and a part month is charged as a whole month — so ${monthsLabel(
              fullMonths,
            )} plus 1 = ${months}. Interest is therefore paid up to ${day(
              p.paid_up_to_date,
            )}.`,
          }
        : {
            title: `${plural(extra, "day")} past the due date`,
            detail: `The due date was ${due}. Interest is charged day-wise up to ${day(
              p.upto_date,
            )} — ${plural(num(p.days), "day")} in all.`,
          };
    case "daily-actual-days":
      return {
        title: "Charged day-wise",
        detail: `Interest is charged for the ${plural(
          num(p.days),
          "day",
        )} from ${ref} to ${day(p.upto_date)}.`,
      };
    default:
      return {
        title: monthly
          ? `${months} charged`
          : `${plural(num(p.days), "day")} charged`,
        detail: `Charged from ${ref}.`,
      };
  }
};

// ── The charge lines (what each amount is made of) ───────────────────────────
// Monthly mode gets the exact arithmetic (P × rate% × months). Daily mode is
// deliberately not turned into a single formula: a complete calendar month is
// charged as one month's interest and only the leftover days are per-day, so a
// made-up "days × rate" line would not reproduce the total.
const chargeLines = (p) => {
  const monthly = p.rules?.monthly !== false;
  const principal = num(p.principal_balance);
  const months = num(p.months_charged);
  const lines = [];

  const periodInterest = num(p.period_interest);
  const periodInsurance = num(p.period_insurance);
  const intRate = num(p.applied_interest_rate);
  const insRate = num(p.applied_insurance_rate);

  // Zero-charge branches (prepaid first month, already covered, grace) have
  // nothing to itemise — a "× 0 months = ₹0" line would only confuse. The rule
  // note above already says why nothing is charged.
  if (periodInterest > 0) {
    lines.push({
      label: "Interest for this period",
      formula: monthly
        ? `${money(principal)} × ${pct(intRate)} per month × ${monthsLabel(
            months,
          )}`
        : `${money(principal)} at ${pct(intRate)} per month, for ${plural(
            num(p.days),
            "day",
          )}`,
      amount: periodInterest,
    });
  }
  if (insRate > 0 && periodInsurance > 0) {
    lines.push({
      label: "Insurance for this period",
      formula: monthly
        ? `${money(principal)} × ${pct(insRate)} per month × ${monthsLabel(
            months,
          )}`
        : `${money(principal)} at ${pct(insRate)} per month, for ${plural(
            num(p.days),
            "day",
          )}`,
      amount: periodInsurance,
    });
  }
  if (num(p.pending_interest) > 0) {
    lines.push({
      label: "Interest left unpaid earlier",
      formula: "Carried forward from previous receipts",
      amount: num(p.pending_interest),
    });
  }
  if (num(p.pending_insurance) > 0) {
    lines.push({
      label: "Insurance left unpaid earlier",
      formula: "Carried forward from previous receipts",
      amount: num(p.pending_insurance),
    });
  }
  if (lines.length === 0) {
    lines.push({
      label: "Nothing due for this period",
      formula: "No interest or insurance is chargeable on this date",
      amount: 0,
    });
  }
  return lines;
};

// ============================================================================
// buildReceiptBreakdown — the model both the dialog and the printout render.
// ----------------------------------------------------------------------------
//   preview  – a /receipts/calculate-interest payload (or the local mirror).
//   entered  – { total, otherCharges, discount } as typed on the screen.
//   context  – { voucher, customerName, mobile, storeName, schemeName,
//                loanAmount, editing, receiptNo }.
// Returns null when there is nothing to explain (no loan / preview loaded).
// ============================================================================
export const buildReceiptBreakdown = (preview, entered = {}, context = {}) => {
  if (!preview) return null;

  const total = num(entered.total);
  const charges = num(entered.otherCharges);
  const discount = num(entered.discount);
  const interestDue = num(preview.interest_receivable);
  const insuranceDue = num(preview.insurance_receivable);
  const principal = num(preview.principal_balance);

  // Money that reaches the loan (other charges are collected on top).
  const towardsLoan = Math.max(0, total - charges);
  // Same allocation the backend books with — interest → insurance → principal.
  const alloc = allocatePayment(
    { interestDue, insuranceDue, principal },
    towardsLoan,
    { interestDiscount: discount },
  );

  const rule = explainRule(preview);
  const lines = chargeLines(preview);
  const totalDue = Math.max(0, interestDue - discount) + insuranceDue;

  return {
    header: {
      voucher: context.voucher || "—",
      receiptNo: context.receiptNo || null,
      editing: !!context.editing,
      customerName: context.customerName || "—",
      mobile: context.mobile || "",
      storeName: context.storeName || "",
      schemeName: context.schemeName || "",
      receiptDate: preview.upto_date || null,
      loanDate: preview.loan_date || null,
      loanAmount: num(context.loanAmount) || num(preview.loan_amount),
      approximate: !!preview._local,
    },
    // The dates and counts the charge rests on — the "why this many months".
    basis: [
      { label: "Loan given on", value: day(preview.loan_date) },
      {
        label: "Interest was paid up to",
        value: day(preview.reference_date),
        note: preview.one_month_deduct
          ? "First month was collected at pledge time"
          : null,
      },
      { label: "Next due date", value: day(preview.due_date) },
      { label: "Paying on", value: day(preview.upto_date) },
      {
        label: "Paid after the due date by",
        value:
          num(preview.extra_days) > 0
            ? plural(num(preview.extra_days), "day")
            : "—",
      },
      {
        label: "Charged for",
        value:
          preview.rules?.monthly !== false
            ? monthsLabel(preview.months_charged)
            : plural(num(preview.days), "day"),
        strong: true,
      },
      {
        label: "Outstanding principal",
        value: money(principal),
      },
    ],
    rule,
    lines,
    totals: {
      interestDue,
      insuranceDue,
      discount,
      charges,
      totalDue,
      // What must be collected in all (dues + charges − discount).
      payable: Math.max(0, totalDue + charges),
      received: total,
    },
    // The allocation as plain numbers (for callers that render their own rows,
    // e.g. the receipt summary rail) — same figures as `allocation` below.
    applied: {
      interest: alloc.interestDeduction,
      insurance: alloc.insuranceDeduction,
      principal: alloc.principalReduction,
      charges,
      excess: alloc.excessAmount,
    },
    // How the money received is applied, in the engine's fixed order.
    allocation: [
      { label: "Towards interest", amount: alloc.interestDeduction },
      ...(num(preview.applied_insurance_rate) > 0 || insuranceDue > 0
        ? [{ label: "Towards insurance", amount: alloc.insuranceDeduction }]
        : []),
      ...(alloc.principalReduction > 0
        ? [{ label: "Towards principal", amount: alloc.principalReduction }]
        : []),
      ...(charges > 0 ? [{ label: "Other charges", amount: charges }] : []),
      ...(alloc.excessAmount > 0
        ? [
            {
              label: "Excess returned / on account",
              amount: alloc.excessAmount,
            },
          ]
        : []),
    ],
    outcome: {
      paidUpToDate: preview.paid_up_to_date,
      principalAfter: Math.max(0, principal - alloc.principalReduction),
      shortfallInterest: alloc.shortfallInterest,
      shortfallInsurance: alloc.shortfallInsurance,
      shortfall: alloc.shortfallInterest + alloc.shortfallInsurance,
    },
  };
};

// ── Printable page ──────────────────────────────────────────────────────────
// Self-contained HTML (inline styles, no assets) opened in a new window and
// printed, so a customer can be handed the same explanation they were shown.
const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

export const receiptBreakdownPrintHtml = (model) => {
  if (!model) return "";
  const { header, basis, rule, lines, totals, allocation, outcome } = model;

  const row = (label, value, opts = {}) => `
    <tr>
      <td style="padding:4px 0;color:#475569;${
        opts.strong ? "font-weight:700;" : ""
      }">${esc(label)}${
    opts.note
      ? `<div style="font-size:10px;color:#94A3B8">${esc(opts.note)}</div>`
      : ""
  }</td>
      <td style="padding:4px 0;text-align:right;font-family:monospace;${
        opts.strong ? "font-weight:700;" : ""
      }">${esc(value)}</td>
    </tr>`;

  const chargeRow = (l) => `
    <tr>
      <td style="padding:6px 0;border-bottom:1px solid #E2E8F0">
        <div style="font-weight:600">${esc(l.label)}</div>
        <div style="font-size:10.5px;color:#64748B">${esc(l.formula)}</div>
      </td>
      <td style="padding:6px 0;border-bottom:1px solid #E2E8F0;text-align:right;font-family:monospace;font-weight:700">${esc(
        money(l.amount),
      )}</td>
    </tr>`;

  return `<!doctype html>
<html><head><meta charset="utf-8" /><title>Payment Calculation — ${esc(
    header.voucher,
  )}</title></head>
<body style="font-family:'Segoe UI',Roboto,Arial,sans-serif;font-size:12px;color:#0F172A;margin:0;padding:18px;max-width:640px">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #0F172A;padding-bottom:8px">
    <div>
      <div style="font-size:16px;font-weight:800">${esc(
        header.storeName || "Payment Calculation",
      )}</div>
      <div style="color:#64748B">How this amount was arrived at</div>
    </div>
    <div style="text-align:right">
      <div><strong>Ticket:</strong> ${esc(header.voucher)}</div>
      <div><strong>Date:</strong> ${esc(day(header.receiptDate))}</div>
    </div>
  </div>

  <table style="width:100%;margin-top:10px;border-collapse:collapse">
    ${row("Customer", header.customerName)}
    ${header.mobile ? row("Mobile", header.mobile) : ""}
    ${header.loanAmount ? row("Loan amount", money(header.loanAmount)) : ""}
    ${header.schemeName ? row("Scheme", header.schemeName) : ""}
  </table>

  <div style="margin-top:14px;padding:8px 10px;background:#EFF6FF;border:1px solid #DBEAFE;border-radius:8px">
    <div style="font-weight:700">${esc(rule.title)}</div>
    <div style="color:#334155;margin-top:2px">${esc(rule.detail)}</div>
  </div>

  <div style="margin-top:14px;font-weight:700;text-transform:uppercase;font-size:10.5px;letter-spacing:.06em;color:#64748B">Basis of the calculation</div>
  <table style="width:100%;border-collapse:collapse">
    ${basis.map((b) => row(b.label, b.value, b)).join("")}
  </table>

  <div style="margin-top:14px;font-weight:700;text-transform:uppercase;font-size:10.5px;letter-spacing:.06em;color:#64748B">Amount due</div>
  <table style="width:100%;border-collapse:collapse">
    ${lines.map(chargeRow).join("")}
    ${
      totals.discount > 0
        ? row("Less: interest discount", `− ${money(totals.discount)}`)
        : ""
    }
    ${
      totals.charges > 0 ? row("Add: other charges", money(totals.charges)) : ""
    }
    ${row("Total payable", money(totals.payable), { strong: true })}
  </table>

  <div style="margin-top:14px;font-weight:700;text-transform:uppercase;font-size:10.5px;letter-spacing:.06em;color:#64748B">Amount received &amp; how it is applied</div>
  <table style="width:100%;border-collapse:collapse">
    ${row("Received now", money(totals.received), { strong: true })}
    ${allocation.map((a) => row(a.label, money(a.amount))).join("")}
    ${
      outcome.shortfall > 0
        ? row("Still unpaid (carried forward)", money(outcome.shortfall), {
            strong: true,
          })
        : ""
    }
  </table>

  <div style="margin-top:14px;padding:8px 10px;background:#ECFDF5;border:1px solid #A7F3D0;border-radius:8px">
    <div><strong>Interest paid up to:</strong> ${esc(
      day(outcome.paidUpToDate),
    )}</div>
    <div><strong>Balance principal:</strong> ${esc(
      money(outcome.principalAfter),
    )}</div>
    ${
      outcome.shortfall > 0
        ? `<div style="color:#92400E"><strong>Unpaid dues carried forward:</strong> ${esc(
            money(outcome.shortfall),
          )}</div>`
        : ""
    }
  </div>

  <div style="margin-top:12px;color:#94A3B8;font-size:10px">
    Computed as per the store's interest rules${
      header.approximate ? " (offline estimate — confirm at the counter)" : ""
    }. This is an explanation of the charge, not a receipt.
  </div>
  <script>window.onload=function(){window.print();}</script>
</body></html>`;
};

export const printReceiptBreakdown = (model) => {
  const html = receiptBreakdownPrintHtml(model);
  if (!html) return;
  const win = window.open("", "_blank", "width=720,height=900");
  if (!win) return;
  win.document.write(html);
  win.document.close();
};
